// Sub-activities (Kareem, 4 Nov): the parts of an activity, at any depth, each with how far along it is. Add one by
// hand here (a title and Enter makes a task owned by you; "Add with details" opens the full form with "Part of" set),
// or ask the assistant ("add a sub-activity to ..."), which proposes a card. The child shows "Part of: Parent".
import { ActLink } from './ActLink';
import { useNewSince } from '../lib/seen';
import { NewDot } from './NewDot';
import { AddButton } from './AddButton';
import { useState, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import { TYPE_LABELS, type ActivityDetail, type SubActivity } from '@dpw/shared';
import { api } from '../api';
import { dayLabel, localDay } from '../lib/format';
import { listItem } from '../lib/motion';
import { Progress, StatusPill } from './bits';
import { useToast } from './toast';

/** How far along a sub-activity is: done, its steps, its own parts, or a little for in progress. */
export function subProgress(s: SubActivity): number {
  if (s.status === 'done') return 100;
  if (s.stepsTotal) return Math.round((100 * s.stepsDone) / s.stepsTotal);
  if (s.subCount) return Math.round((100 * s.subDone) / s.subCount);
  return s.status === 'not_started' ? 0 : Math.max(s.progress, 10);
}
const when = (s: SubActivity) => (s.startsAt ? dayLabel(localDay(s.startsAt)) : s.dueDate ? `due ${dayLabel(s.dueDate)}` : null);
const meta = (s: SubActivity) => [TYPE_LABELS[s.type], s.owner?.split(' ')[0], when(s), s.subCount ? `${s.subCount} ${s.subCount === 1 ? 'part' : 'parts'}` : null].filter(Boolean).join(' · ');

export function SubActivities({ a, canWrite, phone = false, onAddFull }: { a: ActivityDetail; canWrite: boolean; phone?: boolean; onAddFull?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const subs = a.subActivities;
  const dots = useNewSince();
  const done = subs.filter(s => s.status === 'done').length;
  if (!subs.length && !canWrite) return null;
  const add = async (e: FormEvent) => {
    e.preventDefault();
    const v = title.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      await api.activities.create({ trackId: a.trackId, type: 'task', title: v, parentActivityId: a.id });
      setTitle('');
      await qc.invalidateQueries();
    } catch (x) { toast(x instanceof Error ? x.message : 'Could not add it.'); } finally { setBusy(false); }
  };
  const rows = (
    <AnimatePresence initial={false}>
      {subs.map(s => (
        <motion.div key={s.id} role="listitem" layout="position" variants={listItem} initial="initial" animate="animate" exit="exit">
          <ActLink className={phone ? 'ph-rowx subrow' : 'subrow'} id={s.id}>
            <span className="st">
              <span className="t">{dots.row(s.id) && <NewDot />}{s.title}</span>
              <span className="m">{meta(s)}</span>
            </span>
            <span className="pr" aria-label={`${subProgress(s)}% along`}><Progress value={subProgress(s)} color={s.status === 'done' ? 'var(--grn)' : s.status === 'at_risk' || s.status === 'blocked' || s.isOverdue ? 'var(--red)' : 'var(--ink)'} /></span>
            {!phone && <StatusPill status={s.status} overdue={s.isOverdue} />}
          </ActLink>
        </motion.div>
      ))}
    </AnimatePresence>
  );
  const form = canWrite && (
    <form className="subadd" onSubmit={e => void add(e)}>
      <input className="inp" value={title} onChange={e => setTitle(e.target.value)} placeholder="Add a sub-activity, then Enter" aria-label="Add a sub-activity" disabled={busy} />
      {onAddFull && <AddButton className={phone ? '' : 'sm'} onClick={onAddFull}>Add with details</AddButton>}
    </form>
  );
  return (
    <section className={phone ? 'subacts phone' : 'panel subacts'} aria-label="Sub-activities">
      <div className="sh"><b>Sub-activities</b><span>{subs.length ? `${done} of ${subs.length} done` : 'None yet'}</span></div>
      {subs.length > 0 && <div className="subs" role="list">{rows}</div>}
      {form}
    </section>
  );
}
